import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Star } from "lucide-react";

const Hero = () => {
  const quickInfoItems = [
    {
      icon: MapPin,
      label: "Morada",
      value: "Av. Almirante Afonso Cerqueira nº. 67",
      subvalue: "(Ed. Tojal e Tojal) — 3510-023 Viseu",
      link: "https://maps.google.com/?q=Avenida+Almirante+Afonso+Cerqueira+67+Viseu",
    },
    {
      icon: Clock,
      label: "Horário",
      value: "Segunda a Sexta",
      subvalue: "10h às 19h",
    },
    {
      icon: Phone,
      label: "Telefone",
      value: "232 088 790",
      subvalue: "927 525 885 (Urgências)",
      link: "tel:232088790",
    },
  ];

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen pt-20 flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-top bg-no-repeat"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_dentista-viseu/artifacts/2fz5aje7_DSC_2133.JPG')",
          backgroundPosition: "center 30%",
        }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/70 md:to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Rating Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-4 py-2 mb-6"
              data-testid="rating-badge"
            >
              <Star className="w-4 h-4 fill-gray-800 text-gray-800" />
              <span className="font-manrope font-semibold text-sm text-[#1A1A1A]">
                5.0 Avaliação Google
              </span>
            </motion.div>

            {/* Tagline */}
            <p className="font-outfit text-xl md:text-2xl text-[#4A4A4A] mb-4 italic">
              "Deixe o seu sorriso ao nosso cuidado"
            </p>

            <h1
              className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight mb-6"
              data-testid="hero-title"
            >
              Implantologia Avançada e{" "}
              <span className="text-[#4A4A4A]">Ortodontia com Alinhadores Invisíveis</span>
            </h1>

            <p
              className="text-lg text-[#4A4A4A] leading-relaxed mb-8 max-w-xl"
              data-testid="hero-subtitle"
            >
              A sua Clínica de Cirurgia e Reabilitação Oral em Viseu. O seu sorriso está em boas mãos, com uma equipa de profissionais experientes e dedicados à sua saúde oral.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-12">
              <a
                href="tel:927525885"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white rounded-full px-8 py-3.5 font-semibold hover:bg-[#333333] transition-all shadow-lg"
                data-testid="hero-emergency-btn"
              >
                <Phone className="w-5 h-5 text-white" />
                <span className="text-white">Urgência: 927 525 885</span>
              </a>
              <a
                href="#especialidades"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector("#especialidades")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-8 py-3.5 font-semibold hover:bg-gray-100 transition-all"
                data-testid="hero-specialties-btn"
              >
                Ver Especialidades
              </a>
            </div>
          </motion.div>

          {/* Right Column - Quick Info Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-4"
          >
            {quickInfoItems.map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white/70 backdrop-blur-md border border-gray-200 rounded-2xl p-5 flex items-center gap-4 hover:shadow-lg transition-all"
                data-testid={`quick-info-${item.label.toLowerCase()}`}
              >
                <div className="bg-gray-100 w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#1A1A1A]" />
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold tracking-[0.15em] uppercase text-[#8A8A8A] block mb-1">
                    {item.label}
                  </span>
                  {item.link ? (
                    <a
                      href={item.link}
                      target={item.link.startsWith("http") ? "_blank" : undefined}
                      rel={item.link.startsWith("http") ? "noopener noreferrer" : undefined}
                      className="font-manrope font-semibold text-[#1A1A1A] hover:text-[#4A4A4A] transition-colors block"
                      data-testid={`${item.label.toLowerCase()}-link`}
                    >
                      {item.value}
                    </a>
                  ) : (
                    <span className="font-manrope font-semibold text-[#1A1A1A] block">
                      {item.value}
                    </span>
                  )}
                  <span className="text-sm text-[#4A4A4A]">{item.subvalue}</span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 rounded-full border-2 border-[#1A1A1A] flex items-start justify-center p-2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-1.5 h-1.5 rounded-full bg-[#1A1A1A]"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
